import { useContext, Fragment, useEffect, useState } from "react";
import { Button, Menu, MenuItem, Divider, Tooltip } from "@mui/material";
import HorizontalScrollbar from "components/HorizontalScrollbar";
import DomEvents from "events/DomEvents";
import EventsEmitter from "events/EventsEmitter";
import { handleClick } from "actions/ViewerTools";
import commonStyles from "styles/common";
import topStyles from "styles/topPanel";
import {
    Comment,
    Save,
    PanTool,
    AddCircle,
    RemoveCircle,
    CropFree,
    ArrowDropDown,
    NetworkCheck,
    Rotate90DegreesCcw,
    CenterFocusStrong,
    PersonalVideo,
} from "@mui/icons-material";
import { trackEvent } from "components/Tracking";
import { AlignToView, Pers, Ortho, North, South, East, West, DN, UP } from "assets/images/";
import { ViewerEvents, CrossSectionBounds } from "events/index";
import { StatsPanel, CommentsPanel, RightPanel } from "panels/index";
import { useDispatch, useSelector } from 'react-redux';
import {
    setActiveProjectData,
    resetActiveProjectData
} from "store/auth";
import { useJumboTheme } from 'themes/jumbo/@jumbo/components/JumboTheme/hooks';
import useSwalWrapper from 'themes/jumbo/@jumbo/vendors/sweetalert2/hooks';
import { useHistory } from "react-router-dom";
import {ViewerContext} from "views/Viewer";

let globalSliceMode = 0;

function renderDirection(direction: number, classes: any) {
    switch (direction) {
        case 1:
            return <North className={classes.toolIconShiftLeft} />;
        case 2:
            return <South className={classes.toolIconShiftLeft} />;
        case 3:
            return <East className={classes.toolIconShiftLeft} />;
        case 4:
            return <West className={classes.toolIconShiftLeft} />;
        case 5:
            return <UP className={classes.toolIconShiftLeft} />;
        case 6:
            return <DN className={classes.toolIconShiftLeft} />;
        default:
            return <North className={classes.toolIconShiftLeft} />;
    }
}

export default function TopPanel() {
    const { classes, cx } = topStyles();
    const { classes: common } = commonStyles();
    const { newScene, projectId } = ViewerEvents;
    const projectsRef = useSelector((state: any) => state.cache.projectsRef);
    const [sliceMode, setSliceMode] = useState(0);
    const [update, setUpdate] = useState(false);
    const [hasClick, setHasClick] = useState(false);
    const [mode, setMode] = useState(0);
    const [ortho, setOrtho] = useState(0);
    const [clipping, setClipping] = useState(false);
    const [direction, setDirection] = useState(1);
    const [anchorEl, setAnchorEl] = useState(null);
    const [show, setShow] = useState({
        comments: false,
        stats: false,
        slice: false,
        tools: false,
    });
	const storeAuthData = useSelector((state: any) => state.auth);
    const [hasFullAccess, setHasFullAccess] = useState(false);
    const { hasActiveSubscription = false, projectAccessLevel:access_level = "read", activeProject, isDefaultProject = false, defaultProjects } = storeAuthData;
    const history = useHistory();
    const Swal = useSwalWrapper();
    const sweetAlerts = () => {
        Swal.fire({
            title: !hasActiveSubscription ? 'Your account has limited access!' : 'You have limited access!',
            text: !hasActiveSubscription ? 'We have limited your access to some features. Please upgrade your current plan to access this feature.' : 'Only users who are included in the permissions for this project can access to this feature.',
            imageWidth: 450,
            imageHeight: 200,
            imageAlt: 'Custom image',
            confirmButtonText: "Upgrade my plan",
            showCloseButton: true,
        }).then((result) => {
            result.isConfirmed && history.push("/profile");
        });
    };
	const dispatch = useDispatch();
    const { theme } = useJumboTheme();
    const { 
        viewHasChanges
    } = useContext<any>(ViewerContext);

    useEffect(() => {
        setHasFullAccess((hasActiveSubscription && (access_level === 'full' || access_level === 'white')) || storeAuthData.data.ipn_name === 'free')
    }, [activeProject, access_level, hasActiveSubscription, storeAuthData]);
    
    useEffect(() => {
        if(!projectId || !projectsRef)  return;

        const curProject = projectsRef.find((projectRef: any) => projectRef.project_ref === projectId);
        if (!curProject)  {
            dispatch(resetActiveProjectData());
            return;
        };

        dispatch(setActiveProjectData({
            activeProject: projectId,
            projectAccessLevel: curProject.access_level || 'read'
        }));
    }, [projectsRef, projectId])

    useEffect(() => {
        CrossSectionBounds.resetCrossSectionBounds();
        var makeUpdate = (object: any) => {
            setUpdate((prevVal) => !prevVal);
        };
        var updateCrossSection = (presetType: number) => {
            setSliceMode(presetType);
        };

        const onKeyDown = (event: any) => {
            //console.log('event:', event);
            if (event.srcElement.tagName === "INPUT" || event.srcElement.tagName === "TEXTAREA") {
                return;
            }
            let direction = 1;
            switch (event.keyCode) {
                case 76: //L
                    handleClick({ cmd: "sliceMode", param: globalSliceMode });
                    break;
                case 78:
                    direction = 1;
                    setDirection(direction);
                    handleClick({ cmd: "setDirection", param: direction });
                    break;
                case 83:
                    direction = 2;
                    setDirection(direction);
                    handleClick({ cmd: "setDirection", param: direction });
                    break;
                case 69:
                    direction = 3;
                    setDirection(direction);
                    handleClick({ cmd: "setDirection", param: direction });
                    break;
                case 87:
                    direction = 4;
                    setDirection(direction);
                    handleClick({ cmd: "setDirection", param: direction });
                    break;
                case 85: //U
                    direction = 5;
                    setDirection(direction);
                    handleClick({ cmd: "setDirection", param: direction });
                    break;
                case 68: //D
                    direction = 6;
                    setDirection(direction);
                    handleClick({ cmd: "setDirection", param: direction });
                    break;
                case 79: //O
                    setOrtho(1);
                    handleClick({ cmd: "setOrtho", param: 1 });
                    break;
                case 80: //P
                    setOrtho(0);
                    handleClick({ cmd: "setOrtho", param: 0 });
                    break;
            }
        };
        EventsEmitter.on("initview", makeUpdate);
        EventsEmitter.on("newScene", makeUpdate);
        EventsEmitter.on("updateCrossSection", updateCrossSection);
        DomEvents.onKeyDown = onKeyDown;
        return () => {
            EventsEmitter.removeEventListener("newScene", makeUpdate);
            EventsEmitter.removeEventListener("updateCrossSection", updateCrossSection);
            DomEvents.onKeyDown = (event: any) => {};
            EventsEmitter.removeEventListener("initview", makeUpdate);
        };
    }, []);
    //var access_level = "read";
    if (!projectId) {
        return <div />;
    }

    const openMenu = (evt: any) => {
        setAnchorEl(evt.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleChange = (direction: number) => {
        handleClick({ cmd: "setDirection", param: direction });
        setDirection(direction);
        setAnchorEl(null);
    };

    const handleOrtho = (ortho: string) => {
        const val = ortho === "setperspective" ? 0 : 1;
        setOrtho(val);
        handleClick({ cmd: "setOrtho", param: val });
    };

    const handleClipping = (evt: any) => {
        handleClick({ cmd: "sliceMode", param: sliceMode });
        setClipping(!clipping);
    };

    const showPanel = (name: string) => {
        var curShow: any = show;
        var newShow: any = { ...show };
        if (name === "comments" && curShow.comments) {
            var { view, viewer } = ViewerEvents;
            if (view && viewer) {
                var commentId: string = "comment:" + view.ID;
                viewer.getViewer().hideGeoJSONLayer(commentId);
            }
        }
        if (name === "tools") {
            newShow = { ...show, tools: false };
        }

        newShow[name] = !curShow[name];
        setShow(newShow);
    };

    const handleClose = (name: string) => {
        showPanel(name);
    };

    return (
        <Fragment>
            <div className={`${classes.topRoot} ${theme.type}-theme`} style={{ userSelect: update ? "none" : "none" }}>
                <HorizontalScrollbar>
                    <div className={classes.secondGroup}>
                        <Tooltip title="Rotate Camera" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Rotate Camera");
                                setMode(0);
                                handleClick({ cmd: "vorbit", param: 0 });
                            }}
                            aria-label="Rotate Camera"
                            className={cx(common.button, common.spacing)}
                        >
                            <Rotate90DegreesCcw className={mode === 0 ? common.selected : common.default} />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Pan" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Pan");
                                setMode(1);
                                handleClick({ cmd: "pan", param: 1 });
                            }}
                            aria-label="Pan"
                            className={cx(common.button, common.spacingRight)}
                        >
                            <PanTool className={mode === 1 ? common.selected : common.default} />
                        </Button></Tooltip>{" "}
                        
                        <Tooltip title="Zoom In" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Zoom In");
                                handleClick({ cmd: "zoomin", param: -300 });
                            }}
                            aria-label="Zoom In"
                            className={cx(common.button, common.spacingRight)}
                        >
                            <AddCircle className={common.default} />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Zoom Out" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Zoom Out");
                                handleClick({ cmd: "zoomout", param: 300 });
                            }}
                            aria-label="Zoom Out"
                            className={cx(common.button, common.spacingRight)}
                        >
                            <RemoveCircle className={common.default} />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Direction" placement="top" arrow>
                        <Button
                            id="directionButton"
                            onClick={(evt) => {
                                trackEvent("Viewer", "TopPanel", "Direction");
                                openMenu(evt);
                            }}
                            aria-label="Direction"
                            className={cx(common.button, common.spacingRight)}
                        >
                            {renderDirection(direction, classes)}
                            <ArrowDropDown className={classes.dropDownIcon} />
                        </Button>
                        </Tooltip>
                        <Menu
                            id="directionMenu"
                            className={cx(classes.directionMenu, common.spacing)}
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            style={{
                                marginLeft: 5,
                                width: 84,
                                maxWidth: 320,
                                top: 10,
                            }}
                        >
                            <MenuItem
                                className={classes.menuItem}
                                title="North"
                                value={1}
                                onClick={(e) => {
                                    trackEvent("Viewer", "TopPanel", "Direction", "North");
                                    handleChange(1);
                                }}
                            >
                                <North className={classes.toolIcon} />
                            </MenuItem>
                            <MenuItem
                                className={classes.menuItem}
                                title="South"
                                value={2}
                                onClick={(e) => {
                                    trackEvent("Viewer", "TopPanel", "Direction", "South");
                                    handleChange(2);
                                }}
                            >
                                <South className={classes.toolIcon} />
                            </MenuItem>
                            <MenuItem
                                className={classes.menuItem}
                                title="East"
                                value={3}
                                onClick={(e) => {
                                    trackEvent("Viewer", "TopPanel", "Direction", "East");
                                    handleChange(3);
                                }}
                            >
                                <East className={classes.toolIcon} />
                            </MenuItem>
                            <MenuItem
                                className={classes.menuItem}
                                title="West"
                                value={4}
                                onClick={(e) => {
                                    trackEvent("Viewer", "TopPanel", "Direction", "West");
                                    handleChange(4);
                                }}
                            >
                                <West className={classes.toolIcon} />
                            </MenuItem>
                            <MenuItem
                                className={classes.menuItem}
                                title="Up"
                                value={5}
                                onClick={(e) => {
                                    trackEvent("Viewer", "TopPanel", "Direction", "Up");
                                    handleChange(5);
                                }}
                            >
                                <UP className={classes.toolIcon} />
                            </MenuItem>
                            <MenuItem
                                className={classes.menuItem}
                                title="Down"
                                value={6}
                                onClick={(e) => {
                                    trackEvent("Viewer", "TopPanel", "Direction", "Down");
                                    handleChange(6);
                                }}
                            >
                                <DN className={classes.toolIcon} />
                            </MenuItem>
                            <MenuItem
                                onClick={(evt) => {
                                    trackEvent("Viewer", "TopPanel", "Direction", "Align clipping plane");
                                    handleClipping(evt);
                                }}
                                title="Align clipping plane"
                                className={
                                    show.slice
                                        ? cx(
                                              classes.menuItem,
                                              sliceMode === 0 || sliceMode === 1 ? common.disabledGrayEvents : ""
                                          )
                                        : cx(
                                              classes.menuItem,
                                              sliceMode === 0 || sliceMode === 1 ? common.disabledGrayEvents : ""
                                          )
                                }
                            >
                                <AlignToView className={classes.toolIcon} />
                            </MenuItem>
                        </Menu>
                        <Tooltip title="Orthographic Projection" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Orthographic Projection");
                                handleOrtho("setortho");
                            }}
                            aria-label="Orthographic Projection"
                            color={ortho === 1 ? "primary" : "inherit"}
                            className={cx(common.button, common.spacingRight)}
                        >
                            <Ortho className={cx(classes.toolIcon, ortho === 1 ? common.selected : common.default)} />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Perspective Projection" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Perspective Projection");
                                handleOrtho("setperspective");
                            }}
                            aria-label="Perspective Projection"
                            color={ortho === 0 ? "primary" : "inherit"}
                            className={cx(common.button, common.spacingRight)}
                        >
                            <Pers
                                className={[classes.toolIcon, ortho === 0 ? common.selected : common.default].join(" ")}
                            />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Zoom to Extents" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Zoom to Extents");
                                handleClick({ cmd: "zoomextents" });
                            }}
                            aria-label="Zoom to Extents"
                            className={cx(common.button, common.spacingRight)}
                        >
                            <CenterFocusStrong className={common.default} />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Full screen" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Full screen");
                                handleClick({ cmd: "fullscreen" });
                            }}
                            aria-label="Full screen"
                            className={cx(common.button, common.spacingRight)}
                        >
                            <CropFree className={common.default} />
                        </Button>
                        </Tooltip>
                    </div>
                    <div className={classes.thirdGroup}>
                        <Tooltip title="Save Changes" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Save Changes");
                                if (isDefaultProject || !hasFullAccess) {
                                    sweetAlerts();
                                    return;
                                }
                                const  { view } = ViewerEvents;

                                handleClick({ cmd: "save" }, () => {
                                    if (view?.thumb && !view.thumb.includes('/app/static/media/default'))  return;
                                    handleClick({ cmd: "changeThumbnail" }, () => {
                                        setTimeout(() => {
                                            handleClick({ cmd: "save" });
                                        }, 250);
                                    })
                                });
                            }}
                            aria-label="Save Changes"
                            className={cx(
                                common.button,
                                common.spacing,
                                !hasFullAccess || !viewHasChanges ? common.disabledGrayEvents : ""
                            )}
                            style={{ width: 70 }}
                            disabled={!hasFullAccess || !viewHasChanges}
                            color="inherit"
                        >
                            <Save
                                className={cx(common.default, !hasFullAccess || !viewHasChanges ? common.disabledGrayEvents : "")}
                                style={{ marginRight: 10 }}
                            />
                            <div style={{ color: !hasFullAccess || !viewHasChanges ? "silver" : "white", marginTop: 2 }}>
                                Save
                            </div>
                        </Button>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem className={classes.divider} />
                        <Tooltip title="Viewer Tools" placement="top" arrow>
                        <Button
                            aria-label="Viewer Tools"
                            onClick={() => {
                                trackEvent("Viewer", "TopPanel", "Viewer Tools");
                                showPanel("tools");
                            }}
                            className={
                                show.tools
                                    ? cx(common.button, common.buttonOutline, common.spacing)
                                    : cx(common.button, common.spacing)
                            }
                        >
                            <PersonalVideo className={show.tools ? common.selected : common.default} />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Show Comments" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Show Comments");
                                showPanel("comments");
                            }}
                            aria-label="Show Comments"
                            disabled={newScene && hasFullAccess ? false : true}
                            className={cx(common.button, common.spacingRight)}
                        >
                            <Comment
                                className={newScene && hasFullAccess ? (show.comments ? common.selected : common.default) : common.grey}
                            />
                        </Button>
                        </Tooltip>
                        <Tooltip title="Stats" placement="top" arrow>
                        <Button
                            onClick={(e) => {
                                trackEvent("Viewer", "TopPanel", "Stats");
                                showPanel("stats");
                            }}
                            aria-label="Stats"
                            className={
                                show.stats
                                    ? cx(common.button, common.buttonOutline, common.spacingRight)
                                    : cx(common.button, common.spacingRight)
                            }
                        >
                            <NetworkCheck className={show.stats ? common.selected : common.default} />
                        </Button>
                        </Tooltip>
                    </div>
                </HorizontalScrollbar>
            </div>
            <StatsPanel show={show.stats} />
            {show.tools ? <RightPanel slideLeft={show.comments} handleClose={() => showPanel("tools")} /> : ""}
            {show.comments ? <CommentsPanel newScene={newScene} handleClose={handleClose} /> : ""}
        </Fragment>
    );
}
